/**
 * Facebook / Instagram / TikTok iOS in-app browsers inject a LCP logger that
 * calls `window.webkit.messageHandlers.*.postMessage`. That bridge only exists
 * when the native app registered WKScriptMessageHandlers. Without it, Safari
 * throws: `undefined is not an object (evaluating 'window.webkit.messageHandlers')`.
 *
 * Install a no-op bridge when missing. Never replace a real native bridge.
 */
type WebkitStubTarget = {
  navigator?: { userAgent?: string; platform?: string; maxTouchPoints?: number }
  webkit?: { messageHandlers?: unknown }
}

export function installIosWebkitMessageHandlerStub(target: WebkitStubTarget): void {
  const nav = target.navigator
  const ua = nav?.userAgent || ''
  const isIos = /iP(hone|ad|od)/.test(ua)
    || (nav?.platform === 'MacIntel' && (nav.maxTouchPoints || 0) > 1)
  if (!isIos) return

  const webkit = (target.webkit ||= {})
  if (webkit.messageHandlers) return

  const store: Record<string, { postMessage: (...args: unknown[]) => void }> = {}
  webkit.messageHandlers = new Proxy(store, {
    get(obj, prop) {
      if (typeof prop === 'symbol' || prop === 'then') return undefined
      const key = String(prop)
      if (key in obj) return obj[key]
      const stub = { postMessage() {} }
      obj[key] = stub
      return stub
    },
  })
}

/** Inline <head> copy — must run before in-app-browser LCP callbacks. */
export const iosWebkitBridgeStubScript = `(function(){try{var n=navigator,ua=n.userAgent||"",ios=/iP(hone|ad|od)/.test(ua)||(n.platform==="MacIntel"&&n.maxTouchPoints>1);if(!ios)return;var w=window;if(!w.webkit)w.webkit={};if(w.webkit.messageHandlers)return;var s={};w.webkit.messageHandlers=new Proxy(s,{get:function(o,p){if(typeof p==="symbol"||p==="then")return;var k=String(p);if(k in o)return o[k];var t={postMessage:function(){}};o[k]=t;return t}})}catch(e){}})();`
